<script setup lang="ts">
import { computed } from 'vue';

import type { HotelDetailsEventSource } from '@apps/main/new-desktop/core/models';
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';

import { isFeatureToggleActive } from '@libs/feature-toggle';

import type { Stay } from '../../models';
import { useHotelDetailsGalleryStore } from '../../stores';

import HotelStayImage from './HotelStayImage.vue';
import HotelStayRoom from './HotelStayRoom.vue';

interface HotelStayDescriptionProps {
  stay: Stay;
  source: HotelDetailsEventSource;
}
const props = defineProps<HotelStayDescriptionProps>();

interface HotelStayDescriptionEmits {
  (eventName: 'more-information'): void;
  (eventName: 'open-gallery', index: number): void;
}

const emit = defineEmits<HotelStayDescriptionEmits>();

const statsigEventsStore = useStatsigEventsStore();
const hotelDetailsGalleryStore = useHotelDetailsGalleryStore();

const imageUrl = computed<string | undefined>(() => {
  if (
    props.stay.imagesURL.length > 0 &&
    props.stay.imagesURL[0] !== undefined
  ) {
    const url = hotelDetailsGalleryStore.getImageUrlWithQuality(
      props.stay.imagesURL[0],
      'small'
    );
    return url;
  }

  return undefined;
});

const imagesCount = computed<number>(() => props.stay.imagesURL.length);

function openGallery(): void {
  statsigEventsStore.logHotelDetailsRoomGalleryOpened({ source: props.source });
  emit('open-gallery', 0);
}
</script>

<template>
  <div class="hotel-stay-description">
    <HotelStayImage
      v-if="!!imageUrl"
      :image-url="imageUrl"
      :should-show-open-gallery-button="imagesCount > 1"
      :initial-slide="0"
      @open-gallery="openGallery"
    />

    <div class="rooms">
      <HotelStayRoom
        v-for="(room, index) in props.stay.rooms"
        :key="index"
        :room="room"
        @more-information="emit('more-information')"
      />

      <div v-if="isFeatureToggleActive('dev-info')">
        <textarea
          :value="
            JSON.stringify(
              {
                isRefundable: props.stay.stayId.isRefundable,
                selectionType: props.stay.selectionType,
                rooms: props.stay.rooms.map((room) => ({
                  roomCode: room.roomCode,
                  roomTypeId: room.roomTypeId,
                  boardBasis: room.boardBasis,
                })),
              },
              null,
              2
            )
          "
          rows="12"
          wrap="off"
          readonly
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-stay-description {
  display: flex;
  gap: var(--spacing-16);
  width: 100%;

  @include for-less-or-equal-than-599-px {
    min-width: -webkit-fill-available;
    min-width: fill-available;
    flex-flow: column;
  }

  .rooms {
    width: -webkit-fill-available;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-24);
  }
}
</style>
